import React, { useEffect, useRef, useState } from "react";
import { Upload, Download, History, FileSpreadsheet, CheckCircle2, AlertTriangle } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatNlDate } from "@/components/Badges";

// Import toolbar: template download, two-step import dialog, and history.
export const ImportAssets = ({ onImported }) => {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [runs, setRuns] = useState([]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/assets/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "TD-Ops_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err, "Template downloaden mislukt."));
    }
  };

  const openImport = () => {
    setFile(null);
    setPreview(null);
    setOpen(true);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/assets/import/preview", fd, { timeout: 0 });
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Voorbeeld mislukt."));
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/assets/import/commit", fd, { timeout: 0 });
      const c = data.counts;
      toast.success(`Import klaar: ${c.new} nieuw, ${c.updated} bijgewerkt, ${c.unchanged} ongewijzigd`);
      setOpen(false);
      onImported?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.errors) {
        setPreview({ valid: false, counts: { new: 0, updated: 0, unchanged: 0 }, errors: detail.errors });
        toast.error(detail.message || "Import geweigerd.");
      } else {
        toast.error(getErrorMessage(err, "Import mislukt."));
      }
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/import-runs");
      setRuns(data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Geschiedenis laden mislukt."));
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="secondary" data-testid="assets-template-button" onClick={downloadTemplate}>
        <Download size={18} className="mr-2" /> Template downloaden
      </Button>
      <Button variant="secondary" data-testid="assets-import-button" onClick={openImport}>
        <Upload size={18} className="mr-2" /> Importeren
      </Button>
      <Button variant="ghost" size="icon" title="Importgeschiedenis" data-testid="assets-import-history-button" onClick={openHistory}>
        <History size={18} />
      </Button>

      {/* Import dialog (two-step: upload+preview, then confirm) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" srDescription="Importeer assets uit een .xlsx-bestand. Je ziet eerst een controle-overzicht voordat er iets wordt opgeslagen.">
          <DialogHeader><DialogTitle>Assets importeren</DialogTitle></DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept=".xlsx" data-testid="import-file-input" onChange={onPickFile} className="hidden" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}>
                <FileSpreadsheet size={18} className="mr-2" /> Kies .xlsx-bestand
              </Button>
              {file && <span className="truncate text-body text-muted-foreground">{file.name}</span>}
            </div>

            {busy && !preview && <p className="text-body text-muted-foreground">Bezig met controleren...</p>}

            {preview && (
              <div className="space-y-3" data-testid="import-preview">
                {preview.valid ? (
                  <div className="flex flex-wrap gap-3 text-body">
                    <span className="inline-flex items-center gap-1 rounded-md border border-goed-rand bg-goed-vlak px-2 py-1 text-goed-tekst"><CheckCircle2 size={14} /> Nieuw: <b>{preview.counts.new}</b></span>
                    <span className="rounded-md border border-wacht-rand bg-wacht-vlak px-2 py-1 text-wacht-tekst">Bijwerken: <b>{preview.counts.updated}</b></span>
                    <span className="rounded-md border border-border bg-muted px-2 py-1 text-foreground">Ongewijzigd: <b>{preview.counts.unchanged}</b></span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-fout-rand bg-fout-vlak px-3 py-2 text-body text-fout-tekst">
                    <AlertTriangle size={18} /> Er zijn fouten gevonden. Er wordt niets geïmporteerd totdat deze zijn opgelost.
                  </div>
                )}

                {preview.errors?.length > 0 && (
                  <div className="max-h-56 overflow-auto rounded-md border border-border">
                    <Table data-testid="import-errors-table">
                      <TableHeader>
                        <TableRow><TableHead className="w-16">Rij</TableHead><TableHead className="w-40">Kolom</TableHead><TableHead>Melding</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.errors.map((e, i) => (
                          <TableRow key={i}>
                            <TableCell className="tabular-nums">{e.row}</TableCell>
                            <TableCell className="font-mono text-data">{e.column}</TableCell>
                            <TableCell className="text-body">{e.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button
              data-testid="import-confirm-button"
              onClick={confirmImport}
              disabled={busy || !preview || !preview.valid}
            >
              Bevestigen en importeren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" srDescription="Overzicht van eerder uitgevoerde imports met datum, gebruiker en resultaat.">
          <DialogHeader><DialogTitle>Importgeschiedenis</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-auto">
            <Table data-testid="import-runs-table">
              <TableHeader>
                <TableRow><TableHead>Datum</TableHead><TableHead>Door</TableHead><TableHead>Bestand</TableHead><TableHead>Resultaat</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {runs.length === 0 && <TableRow><TableCell colSpan={4} className="py-6 text-center text-body text-muted-foreground">Nog geen imports uitgevoerd.</TableCell></TableRow>}
                {runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-body">{formatNlDate(r.created_at?.slice(0, 10))}</TableCell>
                    <TableCell className="text-body">{r.created_by_email}</TableCell>
                    <TableCell className="text-body">{r.filename}</TableCell>
                    <TableCell className="text-body">{r.counts.new} nieuw / {r.counts.updated} bijgewerkt / {r.counts.unchanged} ongewijzigd</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter><Button onClick={() => setHistoryOpen(false)}>Sluiten</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
